import { apiClient } from './apiClient';
import { Logger } from '../utils/logger';

export interface ReferralRewardItem {
  coupon_code: string;
  amount_cents: number;
  created_at: string | null;
  applied_count: number;
  max_redemptions: number;
  stripe_coupon_id: string | null;
}

export interface Invoice {
  id: string;
  // V4-BILLING-DECOMMISSION-LAGO — référence facture provider-neutre (Stripe / Lago historique).
  invoice_ref?: string | null;
  invoice_number: string | null;
  status: string;
  amount_cents: number;
  currency: string;
  issuing_date: string | null;
  pdf_url: string | null;
  coupon_code_used: string | null;
  created_at: string | null;
}

export interface SubscriptionInfo {
  plan_name: string;
  plan_display_name: string;
  status: string;
  started_at: string | null;
  ending_at: string | null;
}

export interface ApplyCouponResult {
  success: boolean;
  message: string;
}

export interface PlanItem {
  id: string;
  name: string;
  display_name: string;
  monthly_subscription_price: number;
  annual_subscription_price: number | null;
  stripe_annual_price_id: string | null;
  monthly_credits: number;
  max_sources: number | null;
  max_channels: number | null;
  has_watermark: boolean;
  can_purchase_topup: boolean;
  topup_credits_per_unit: number | null;
  topup_price_cents: number | null;
  included_seats: number;
  is_agency_plan: boolean;
  agency_extra_seat_credits: number | null;
  agency_extra_seat_price_cents: number | null;
  stripe_price_id: string | null;
}

export interface ValidateCouponResult {
  valid: boolean;
  coupon_name: string | null;
  discount_amount_cents: number;
  final_amount_cents: number;
}

export const billingService = {
  async getInvoices(): Promise<Invoice[]> {
    Logger.debug('[BillingService] Fetching invoices');
    // V4-BILLING-INVOICES-LIST-SHAPE-MISMATCH — le backend renvoie un tableau brut
    // (response_model=List[InvoiceListItem]), jamais {invoices: [...]}.
    const response = await apiClient.get<Invoice[]>('/billing/invoices');
    const invoices = response.data ?? [];
    Logger.info('[BillingService] Invoices fetched', { count: invoices.length });
    return invoices;
  },

  async getInvoicePdfUrl(invoiceId: string): Promise<string> {
    Logger.debug('[BillingService] Fetching invoice PDF URL', { invoiceId });
    const response = await apiClient.get<{ pdf_url: string }>(`/billing/invoices/${encodeURIComponent(invoiceId)}/pdf`);
    Logger.info('[BillingService] Invoice PDF URL fetched', { invoiceId, hasPdfUrl: !!response.data.pdf_url });
    return response.data.pdf_url;
  },

  async getSubscription(): Promise<SubscriptionInfo> {
    Logger.debug('[BillingService] Fetching subscription');
    const response = await apiClient.get<SubscriptionInfo>('/billing/subscription');
    Logger.info('[BillingService] Subscription fetched', {
      planName: response.data.plan_name,
      status: response.data.status,
    });
    return response.data;
  },

  async applyCoupon(couponCode: string): Promise<ApplyCouponResult> {
    Logger.info('[BillingService] Applying coupon', { couponCode });
    const response = await apiClient.post<ApplyCouponResult>('/billing/apply-coupon', {
      coupon_code: couponCode,
    });
    Logger.info('[BillingService] Coupon apply request completed', {
      couponCode,
      success: response.data.success,
    });
    return response.data;
  },

  async getPlans(): Promise<PlanItem[]> {
    Logger.debug('[BillingService] Fetching billing plans');
    const response = await apiClient.get<PlanItem[]>(`/billing/plans?t=${Date.now()}`);
    const plans = response.data ?? [];
    Logger.info('[BillingService] Billing plans fetched', { count: plans.length });
    return plans;
  },

  async validateCoupon(couponCode: string, planId: string): Promise<ValidateCouponResult> {
    Logger.info('[BillingService] Validating coupon', { couponCode, planId });
    const response = await apiClient.post<ValidateCouponResult>('/billing/validate-coupon', {
      coupon_code: couponCode,
      plan_id: planId,
    });
    Logger.info('[BillingService] Coupon validated', {
      couponCode,
      planId,
      valid: response.data.valid,
    });
    return response.data;
  },

  async switchInterval(billingInterval: 'monthly' | 'annual'): Promise<{ checkout_url: string | null }> {
    Logger.info('[BillingService] Switching billing interval', { billingInterval });
    const response = await apiClient.post<{ checkout_url: string | null }>('/billing/switch-interval', {
      billing_interval: billingInterval,
    });
    Logger.info('[BillingService] Billing interval switch request completed', {
      billingInterval,
      hasCheckoutUrl: !!response.data.checkout_url,
    });
    return response.data;
  },

  async cancelSubscription(): Promise<{ status: string; subscription_ends_at: string | null }> {
    Logger.warn('[BillingService] Cancelling subscription');
    const response = await apiClient.post<{ status: string; subscription_ends_at: string | null }>('/billing/cancel-subscription');
    Logger.info('[BillingService] Subscription cancellation request completed', {
      status: response.data.status,
      subscriptionEndsAt: response.data.subscription_ends_at,
    });
    return response.data;
  },

  async upgradePlan(planId: string, billingInterval: 'monthly' | 'annual'): Promise<{ checkout_url: string | null }> {
    Logger.info('[BillingService] Upgrading plan', { planId, billingInterval });
    const response = await apiClient.post<{ checkout_url: string | null }>('/billing/upgrade-plan', {
      plan_id: planId,
      billing_interval: billingInterval,
    });
    Logger.info('[BillingService] Upgrade plan request completed', {
      planId,
      billingInterval,
      hasCheckoutUrl: !!response.data.checkout_url,
    });
    return response.data;
  },

  async deactivateAccount(password: string): Promise<{ success: boolean }> {
    Logger.warn('[BillingService] Deactivating account');
    const response = await apiClient.post<{ success: boolean }>('/auth/deactivate-account', { password });
    Logger.info('[BillingService] Account deactivation request completed', {
      success: response.data.success,
    });
    return response.data;
  },

  async getMyReferralRewards(): Promise<ReferralRewardItem[]> {
    Logger.debug('[BillingService] Fetching referral rewards');
    const response = await apiClient.get<ReferralRewardItem[]>('/referral/my-rewards');
    Logger.info('[BillingService] Referral rewards fetched', { count: response.data.length });
    return response.data;
  },

  async applyRewardToSubscription(couponCode: string): Promise<void> {
    Logger.info('[BillingService] Applying referral reward to subscription', { couponCode });
    await apiClient.post('/referral/apply-to-subscription', { coupon_code: couponCode });
    Logger.info('[BillingService] Referral reward applied to subscription', { couponCode });
  },

  /**
   * Crée une session Stripe Customer Portal et retourne l'URL de redirection.
   * [BETA] Inutilisable pour les beta users (pas de stripe_customer_id) — le backend
   * retourne 400. Appeler uniquement si user.stripePaymentStatus est défini.
   */
  async createPortalSession(): Promise<{ url: string }> {
    Logger.debug('[BillingService] Creating Stripe Customer Portal session');
    const response = await apiClient.post<{ url: string }>('/billing/portal-session');
    Logger.info('[BillingService] Portal session created');
    return response.data;
  },
};
